import React, { useCallback, useEffect, useState } from "react";
import { Button, Spin, Table, Tag } from "antd";
import { CalendarOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import SectionCard from "../shared/SectionCard";
import { EditAccess } from "../../../components/accessControl/AccessControl";
import { taskScheduleApis } from "../../../api/TaskSchedule";
import TaskScheduleFormModal from "./TaskScheduleFormModal";

// "Monthly Production Schedule" section on a task: the month-wise phasing of
// the task's work quantity. Read view + Edit entry point (modal). Only
// quantity-based tasks are schedulable; Open tasks get a muted note. The
// 2dp sum-vs-quantity check renders as a green/amber chip — informational only.

interface ScheduleMonth {
  scheduleMonth: string;
  scheduledQty: string;
  varianceReason?: string;
}

const fmtQty = (value: number) =>
  value.toLocaleString("en-IN", { maximumFractionDigits: 2 });

const TaskScheduleSection = ({
  task,
  canEdit,
}: {
  task: any;
  canEdit?: boolean;
}) => {
  const [months, setMonths] = useState<ScheduleMonth[]>([]);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const isQuantityTask =
    (task?.workType || "") !== "Open" &&
    task?.workQuantity !== null &&
    task?.workQuantity !== undefined;
  const workQuantity = Number(task?.workQuantity) || 0;
  const uom = task?.workUomShortCode || task?.workUomName || "";

  const load = useCallback(async () => {
    const taskId = Number(task?.taskId);
    if (!taskId || !isQuantityTask) return;
    try {
      setLoading(true);
      const res = await taskScheduleApis.getByTask(taskId);
      if (res?.status === true) {
        setMonths(Array.isArray(res?.data?.months) ? res.data.months : []);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.taskId, isQuantityTask]);

  useEffect(() => {
    setMonths([]);
    load();
  }, [load]);

  const total = months.reduce((acc, m) => acc + (Number(m.scheduledQty) || 0), 0);
  // ±1 unit is rounding noise (mirrors the backend SCHEDULE_SUM_TOLERANCE).
  const delta = workQuantity > 0 ? Number((total - workQuantity).toFixed(2)) : 0;
  const matches = workQuantity > 0 && Math.abs(delta) <= 1;

  // Planned-window flag at month granularity (mirrors the backend check).
  const startMonth = task?.taskPlannedStartDate && dayjs(task.taskPlannedStartDate).isValid()
    ? dayjs(task.taskPlannedStartDate).format("YYYY-MM")
    : null;
  const endMonth = task?.taskPlannedEndDate && dayjs(task.taskPlannedEndDate).isValid()
    ? dayjs(task.taskPlannedEndDate).format("YYYY-MM")
    : null;
  const isOutOfWindow = (iso: string) => {
    const month = dayjs(iso).format("YYYY-MM");
    return Boolean((startMonth && month < startMonth) || (endMonth && month > endMonth));
  };
  const varianceReasons = Array.from(
    new Set(months.map((m) => m.varianceReason).filter(Boolean))
  ) as string[];

  return (
    <SectionCard
      title={
        <span>
          <CalendarOutlined style={{ marginRight: 6 }} />
          Monthly Production Schedule{" "}
          {months.length ? (
            <span style={{ color: "#8094ae", fontWeight: 400 }}>
              ({months.length} month{months.length === 1 ? "" : "s"})
            </span>
          ) : null}
        </span>
      }
      style={{ marginBottom: 12 }}
      extra={
        isQuantityTask && canEdit ? (
          <EditAccess moduleName="Tasks">
            <Button
              size="small"
              icon={months.length ? <EditOutlined /> : <PlusOutlined />}
              onClick={() => setEditOpen(true)}
            >
              {months.length ? "Edit Schedule" : "Add Schedule"}
            </Button>
          </EditAccess>
        ) : null
      }
    >
      {!isQuantityTask ? (
        <span style={{ color: "#8094ae", fontSize: 12 }}>
          Only quantity-based tasks can be scheduled.
        </span>
      ) : loading ? (
        <Spin size="small" />
      ) : !months.length ? (
        <span style={{ color: "#8094ae", fontSize: 12 }}>
          No monthly schedule yet.
        </span>
      ) : (
        <>
          <Table
            size="small"
            pagination={false}
            rowKey="scheduleMonth"
            dataSource={months}
            columns={[
              {
                title: "Month",
                dataIndex: "scheduleMonth",
                key: "scheduleMonth",
                render: (value: string) => (
                  <span style={{ color: "#022c57", fontWeight: 500 }}>
                    {dayjs(value).format("MMM YYYY")}
                    {isOutOfWindow(value) && (
                      <Tag color="gold" style={{ marginLeft: 6, fontSize: 10, lineHeight: "16px" }}>
                        outside plan
                      </Tag>
                    )}
                  </span>
                ),
              },
              {
                title: `Planned Qty${uom ? ` (${uom})` : ""}`,
                dataIndex: "scheduledQty",
                key: "scheduledQty",
                align: "right" as const,
                render: (value: string) => fmtQty(Number(value) || 0),
              },
            ]}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 8,
              fontSize: 12,
              color: "#526484",
            }}
          >
            <span>
              Scheduled <b style={{ color: "#022c57" }}>{fmtQty(total)}</b>
              {workQuantity > 0 ? (
                <>
                  {" "}
                  of {fmtQty(workQuantity)}
                  {uom ? ` ${uom}` : ""}
                </>
              ) : null}
            </span>
            {workQuantity > 0 &&
              (matches ? (
                <Tag color="green">
                  Fully scheduled
                  {delta !== 0 ? ` (Δ ${delta > 0 ? "+" : ""}${delta})` : ""}
                </Tag>
              ) : (
                <Tag color="gold">
                  Δ {delta > 0 ? "+" : ""}
                  {fmtQty(delta)}
                </Tag>
              ))}
          </div>
          {varianceReasons.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 12, color: "#b58a1f" }}>
              Reason (outside planned dates): {varianceReasons.join("; ")}
            </div>
          )}
        </>
      )}

      <TaskScheduleFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        task={{
          taskId: Number(task?.taskId),
          taskName: task?.taskName,
          workQuantity: task?.workQuantity,
          workUomName: uom,
          plannedStartDate: task?.taskPlannedStartDate,
          plannedEndDate: task?.taskPlannedEndDate,
        }}
        initialMonths={months}
        onSaved={load}
      />
    </SectionCard>
  );
};

export default TaskScheduleSection;
